import { Autocomplete, Box, TextField, Typography } from '@mui/material'
import type { Country } from '../api/types'
import { Flag } from './Flag'
import { tokens } from '../theme'
import { fold } from '../lib/format'

/**
 * Choosing one of 249 countries.
 *
 * A plain select was fine while the list was the handful with a boundary
 * loaded; against the whole ISO register it is a scroll. So: type-ahead,
 * matching on the code as well as the name, and with diacritics folded —
 * somebody typing "cote" or "CI" is looking for Côte d'Ivoire and should
 * not have to know which.
 *
 * Countries the within-country check cannot judge are offered anyway and
 * marked, rather than hidden. Refusing to let anyone name their own country
 * is a worse answer than saying which check will not run.
 */


/**
 * Ranked, not merely filtered.
 *
 * A plain substring match over name-and-code puts Afghanistan above Ghana
 * for "gh", because "gh" is inside "afghanistan". What somebody typing two
 * letters almost always means is the code, and what they mean by a word is
 * the start of a name — so those win, and a fragment buried mid-name comes
 * last rather than not at all.
 */
function rank(country: Country, query: string): number | null {
  const code = country.code.toLowerCase()
  const name = fold(country.name)
  if (code === query) return 0
  if (name.startsWith(query)) return 1
  if (code.startsWith(query)) return 2
  // Any later word: "ivoire" should still reach Côte d'Ivoire.
  if (name.split(/[\s(,-]+/).some((word) => word.startsWith(query))) return 3
  if (name.includes(query)) return 4
  return null
}

function filterCountries(options: Country[], query: string): Country[] {
  const q = fold(query.trim())
  if (!q) return options
  return options
    .map((c) => ({ c, r: rank(c, q) }))
    .filter((x): x is { c: Country; r: number } => x.r !== null)
    .sort((a, b) => a.r - b.r)
    .map((x) => x.c)
}

export function CountryPicker({
  countries, value, onChange, emptyLabel, label, disabled,
}: {
  countries: Country[]
  value: string
  onChange: (code: string) => void
  /**
   * What leaving it empty means, where that is meaningful. Shown under the
   * field — not as the placeholder, which has to tell somebody what to do
   * with a search box, not what happens if they ignore it.
   */
  emptyLabel?: string
  label?: string
  disabled?: boolean
}) {
  const selected = countries.find((c) => c.code === value) ?? null

  return (
    <Box>
    <Autocomplete
      options={countries}
      value={selected}
      disabled={disabled}
      filterOptions={(options, state) => filterCountries(options, state.inputValue)}
      getOptionLabel={(c) => c.name}
      isOptionEqualToValue={(a, b) => a.code === b.code}
      onChange={(_, next) => onChange(next?.code ?? '')}
      size="small"
      fullWidth
      autoHighlight
      openOnFocus
      clearOnBlur
      selectOnFocus
      handleHomeEndKeys
      noOptionsText="No country matches"
      renderOption={(props, c) => (
        <Box component="li" {...props} key={c.code}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, width: '100%' }}>
            <Flag code={c.code} />
            <Box sx={{ flex: 1 }}>{c.name}</Box>
            <Typography
              variant="caption"
              sx={{ color: 'text.disabled', font: `11px ${tokens.font.mono}` }}
            >
              {c.code}
            </Typography>
            {!c.has_boundary && (
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                no boundary
              </Typography>
            )}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={`Search ${countries.length || ''} countries`.replace('  ', ' ')}
          inputProps={{ ...params.inputProps, 'aria-label': label ?? 'Country' }}
          InputProps={{
            ...params.InputProps,
            // The chosen country's flag, so the field reads at a glance
            // once it is closed. Decoration only — the name is still there.
            startAdornment: selected ? (
              <Box sx={{ pl: 1, display: 'flex' }}><Flag code={selected.code} /></Box>
            ) : undefined,
          }}
        />
      )}
      />
      {emptyLabel && !selected && (
        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 1 }}>
          {emptyLabel}
        </Typography>
      )}
    </Box>
  )
}
